import { NotesIcon } from 'pages/ListerVehicleUploadV2/icons/notes';
import styled from 'styled-components';
import { CheckboxList } from './CheckboxList';
import { Section } from 'services/api/vehicle-sections/types';
import { useState } from 'react';
import { Icon } from '@material-ui/core';
import { AudioPlayer } from './AudioPlayer';
import { PlateExpirationDate } from './PlateExpirationDate';

const Wrapper = styled.div<{ isNested?: boolean }>`
  display: flex;
  border-radius: 8px;
  background: #fff;
  flex-direction: column;
  width: 100%;
`;

const InnerWrapper = styled.div<{ isNested?: boolean }>`
  display: flex;
  align-items: center;
  padding: ${props => (props.isNested ? '4px' : '4px')};
  gap: 0px;
  border-radius: 8px;
  background: ${props => (props.isNested ? '#EEF1F4' : 'inherit')};
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: #000;
  line-height: 100%;
`;

const gradeToColor: Record<string, { bg: string; fg: string }> = {
  fair: { bg: '#FFC709', fg: '#000' },
  good: { bg: '#1AB13E', fg: '#fff' },
  poor: { bg: '#D01C2E', fg: '#fff' }
};

const GradeLabel = styled.div<{ bg: string; fg: string }>`
  border: 1.5px solid #ededed;
  background: ${props => props.bg};
  color: ${props => props.fg};
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  padding: 4px 22px;
  border-radius: 100px;
  text-transform: capitalize;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 68px;
`;

const IconWrapper = styled.div`
  display: flex;
`;

const NotesWrapper = styled.div`
  padding: 8px;
`;

const Notes = styled.div`
  background: #2222220a;
  border-radius: 8px;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  color: #000;
  padding: 5px 9px;
  overflow: hidden;
  white-space: initial;
  word-wrap: break-word;
`;

const ExteriorGrade = styled.div`
  color: #666666;
  font-weight: 500;
  font-size: 12px;
  line-height: 100%;
`;

const RightSideWrapper = styled.div`
  margin-left: auto;
  display: flex;
  gap: 12px;
  align-items: center;
`;

const NestedSectionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
`;

export type AdditionalData = {
  numericGrade?: number;
  nestedSections?: Section[];
  expirationDate?: string;
  nestedAdditionalData?: AdditionalData[];
};

type Props = {
  section: Section;
  collapsed?: boolean;
  additionalData?: AdditionalData;
};

const useParsedAdditionalData = (additionalData: AdditionalData) => {
  const hasNumericGrade = typeof additionalData?.numericGrade === 'number';
  const hasNestedSection = !!additionalData?.nestedSections && additionalData.nestedSections.length > 0;
  const hasExpirationDate = typeof additionalData?.expirationDate === 'string';

  return { hasNumericGrade, hasNestedSection, hasExpirationDate };
};

export const GenericSubSection = ({ section, additionalData = {}, collapsed = false }: Props) => {
  const selectedCheckboxes = (section.checkboxes || []).filter(checkbox => checkbox.selected);

  const { hasNumericGrade, hasNestedSection, hasExpirationDate } = useParsedAdditionalData(additionalData);

  const [sectionsExpanded, setSectionsExpanded] = useState(!collapsed);
  const toggleExpanded = () => setSectionsExpanded(curr => !curr);

  const ExpandToggleIcon = () => <Icon>{sectionsExpanded ? 'arrow_drop_down' : 'arrow_drop_up'}</Icon>;

  return (
    <Wrapper>
      <InnerWrapper>
        {!hasNestedSection && (
          <IconWrapper>
            <NotesIcon />
          </IconWrapper>
        )}
        <Label>{section.label}</Label>
        <RightSideWrapper>
          {hasNumericGrade && <ExteriorGrade>{`${additionalData.numericGrade}/32`}</ExteriorGrade>}
          {hasExpirationDate && <PlateExpirationDate date={additionalData.expirationDate!} />}
          {section.grade && <GradeLabel {...gradeToColor[section.grade]}>{section.grade}</GradeLabel>}
          {hasNestedSection && (
            <IconWrapper onClick={toggleExpanded}>
              <ExpandToggleIcon />
            </IconWrapper>
          )}
        </RightSideWrapper>
      </InnerWrapper>
      {hasNestedSection && sectionsExpanded && (
        <NestedSectionsWrapper>
          {additionalData.nestedSections?.map((section, i) => (
            <NestedSection
              additionalData={additionalData.nestedAdditionalData?.[i]}
              section={section}
              key={section.id}
            />
          ))}
        </NestedSectionsWrapper>
      )}
      {section.audio_url && <AudioPlayer url={section.audio_url} />}
      {section.notes?.message && (
        <NotesWrapper>
          <Notes>{section.notes?.message}</Notes>
        </NotesWrapper>
      )}
      {selectedCheckboxes.length > 0 && <CheckboxList checkboxes={selectedCheckboxes} />}
    </Wrapper>
  );
};

const NestedSection = ({ section, additionalData = {} }: { section: Section; additionalData?: AdditionalData }) => {
  const selectedCheckboxes = (section.checkboxes || []).filter(checkbox => checkbox.selected);

  const { hasExpirationDate, hasNumericGrade } = useParsedAdditionalData(additionalData);

  return (
    <Wrapper isNested>
      <InnerWrapper isNested>
        <IconWrapper>
          <NotesIcon />
        </IconWrapper>
        <Label>{section.label}</Label>
        <RightSideWrapper>
          {hasNumericGrade && <ExteriorGrade>{`${additionalData.numericGrade}/32`}</ExteriorGrade>}
          {hasExpirationDate && <PlateExpirationDate date={additionalData.expirationDate!} />}
          {section.grade && <GradeLabel {...gradeToColor[section.grade]}>{section.grade}</GradeLabel>}
        </RightSideWrapper>
      </InnerWrapper>
      {section.notes?.message && (
        <NotesWrapper>
          <Notes>{section.notes?.message}</Notes>
        </NotesWrapper>
      )}
      {selectedCheckboxes.length > 0 && <CheckboxList isNested checkboxes={selectedCheckboxes} />}
    </Wrapper>
  );
};
